import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating, useFocus, useHover, useInteractions, useRole } from '@floating-ui/react';
import { isReactComponent } from '@panwds/react-utils';
import { classNames, twMerge } from '@panwds/tailwindcss';
import React, { Children, cloneElement, useEffect, useRef, useState } from 'react';
import { POPOVER_PLACEMENTS } from '../../utils/constants.js';
export function Tooltip({ children, dataMetrics, dataTestId, label, singleLine = false, addClassName, containerClassName, defaultPlacement = POPOVER_PLACEMENTS.BOTTOM_END, delay, style }) {
    const child = Children.only(children);
    const [placement, setPlacement] = useState(defaultPlacement);
    const [isOpen, setIsOpen] = useState(false);
    const isMounted = useRef(true);
    useEffect(() => () => {
        isMounted.current = false;
    }, []);
    const caretTooltipOffset = 17;
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (open) => isMounted.current && setIsOpen(open),
        placement: defaultPlacement,
        middleware: [
            offset(({ placement, rects }) => {
                const { reference } = rects;
                switch (placement) {
                    case POPOVER_PLACEMENTS.BOTTOM_START:
                    case POPOVER_PLACEMENTS.TOP_START:
                        return { mainAxis: 0, crossAxis: reference.width / 2 - caretTooltipOffset };
                    case POPOVER_PLACEMENTS.BOTTOM_END:
                    case POPOVER_PLACEMENTS.TOP_END:
                        return { mainAxis: 0, crossAxis: -(reference.width / 2 - caretTooltipOffset) };
                    case POPOVER_PLACEMENTS.LEFT_START:
                    case POPOVER_PLACEMENTS.RIGHT_START:
                        return { mainAxis: 0, crossAxis: reference.height / 2 - caretTooltipOffset };
                    case POPOVER_PLACEMENTS.LEFT_END:
                    case POPOVER_PLACEMENTS.RIGHT_END:
                        return { mainAxis: 0, crossAxis: -(reference.height / 2 - caretTooltipOffset) };
                    default:
                        return { mainAxis: 0, crossAxis: 0 };
                }
            }),
            flip(),
            shift(),
            {
                name: 'getPlacement',
                fn: ({ placement }) => {
                    if (isMounted.current) {
                        setPlacement(placement);
                    }
                    return {};
                }
            }
        ],
        whileElementsMounted: autoUpdate
    });
    const hover = useHover(context, { move: true, mouseOnly: true, delay });
    const focus = useFocus(context, { visibleOnly: true });
    const role = useRole(context, {
        role: 'tooltip'
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, role]);
    /**
     * Performance
     *
     * Once we create a popper with createPopper, it stays "alive".
     * This means while scrolling the page, the popper is constantly being updated, even if it is not visible.
     * We can disable the event listeners when the tooltip is hidden to optimize it:
     */
    let defaultStyle = "twds-relative before:twds-content-[''] before:twds-absolute before:twds-h-0 before:twds-w-0 before:twds-border-solid before:twds-border-transparent before:twds-border-8 after:twds-content-[''] after:twds-absolute after:twds-h-0 after:twds-w-0 after:twds-border-solid after:twds-border-transparent after:twds-border-8";
    if (placement === POPOVER_PLACEMENTS.BOTTOM_END) {
        defaultStyle = classNames(defaultStyle, 'twds-top-3 before:twds-right-2 before:-twds-top-2 before:twds-border-t-0 before:twds-border-b-gray-800 after:twds-right-2 after:twds-border-t-0 after:-twds-top-1.5', 'after:twds-border-b-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.BOTTOM_START) {
        defaultStyle = classNames(defaultStyle, 'twds-top-3 before:twds-left-2 before:-twds-top-2 before:twds-border-t-0 before:twds-border-b-gray-800 after:twds-left-2 after:twds-border-t-0 after:-twds-top-1.5', 'after:twds-border-b-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.TOP_START) {
        defaultStyle = classNames(defaultStyle, 'twds-bottom-3 before:twds-left-2 before:-twds-bottom-2 before:twds-border-b-0 before:twds-border-t-gray-800 after:twds-left-2 after:twds-border-b-0 after:-twds-bottom-1.5 ', 'after:twds-border-t-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.TOP_END) {
        defaultStyle = classNames(defaultStyle, 'twds-bottom-3 before:twds-right-2 before:-twds-bottom-2 before:twds-border-b-0 before:twds-border-t-gray-800 after:twds-right-2 after:twds-border-b-0 after:-twds-bottom-1.5', 'after:twds-border-t-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT_START) {
        defaultStyle = classNames(defaultStyle, 'twds-right-3 before:twds-top-2 before:-twds-right-2 before:twds-border-r-0 before:twds-border-l-gray-800 after:twds-top-2 after:twds-border-r-0 after:-twds-right-1.5', 'after:twds-border-l-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT_END) {
        defaultStyle = classNames(defaultStyle, 'twds-right-3 before:twds-bottom-2 before:-twds-right-2 before:twds-border-r-0 before:twds-border-l-gray-800 after:twds-bottom-2 after:twds-border-r-0 after:-twds-right-1.5', 'after:twds-border-l-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT_START) {
        defaultStyle = classNames(defaultStyle, 'twds-left-3 before:twds-top-2 before:-twds-left-2 before:twds-border-l-0 before:twds-border-r-gray-800 after:twds-top-2 after:twds-border-l-0 after:-twds-left-1.5', 'after:twds-border-r-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT_END) {
        defaultStyle = classNames(defaultStyle, 'twds-left-3 before:twds-bottom-2 before:-twds-left-2 before:twds-border-l-0 before:twds-border-r-gray-800 after:twds-bottom-2 after:twds-border-l-0 after:-twds-left-1.5', 'after:twds-border-r-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.BOTTOM) {
        defaultStyle = classNames(defaultStyle, 'twds-top-3 before:twds-right-1/2 before:twds-translate-x-1/2 before:-twds-top-2 before:twds-border-t-0 before:twds-border-b-gray-800 after:twds-right-1/2 after:twds-translate-x-1/2 after:twds-border-t-0 after:-twds-top-1.5 ', 'after:twds-border-b-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.TOP) {
        defaultStyle = classNames(defaultStyle, 'twds-bottom-3 before:twds-right-1/2 before:twds-translate-x-1/2 before:-twds-bottom-2 before:twds-border-b-0 before:twds-border-t-gray-800 after:twds-right-1/2 after:twds-translate-x-1/2 after:twds-border-b-0 after:-twds-bottom-1.5 ', 'after:twds-border-t-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT) {
        defaultStyle = classNames(defaultStyle, 'twds-left-3 before:twds-bottom-1/2 before:twds-translate-y-1/2 before:-twds-left-2 before:twds-border-l-0 before:twds-border-r-gray-800 after:twds-bottom-1/2 after:twds-translate-y-1/2 after:twds-border-l-0 after:-twds-left-1.5 ', 'after:twds-border-r-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT) {
        defaultStyle = classNames(defaultStyle, 'twds-right-3 before:twds-bottom-1/2 before:twds-translate-y-1/2 before:-twds-right-2 before:twds-border-r-0 before:twds-border-l-gray-800 after:twds-bottom-1/2 after:twds-translate-y-1/2 after:twds-border-r-0 after:-twds-right-1.5 ', 'after:twds-border-l-gray-900');
    }
    const className = twMerge('twds-bg-gray-900 [&_*]:twds-text-dark-bg twds-text-dark-bg twds-border twds-border-gray-800', 'twds-font-lato twds-text-xs twds-p-2 twds-rounded twds-shadow-popover dark:twds-shadow-dark-popover twds-border-solid twds-max-w-[230px]', !singleLine && 'twds-break-words', defaultStyle, addClassName);
    const { ...referenceProps } = getReferenceProps();
    return (React.createElement(React.Fragment, null,
        isReactComponent(child.type) ? (React.createElement("div", { ref: refs.setReference, ...referenceProps, className: "twds-flex twds-align-center twds-items-center twds-h-fit twds-w-fit" }, cloneElement(child))) : (cloneElement(child, { ref: refs.setReference, ...referenceProps })),
        React.createElement(FloatingPortal, null,
            React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId || 'tooltip', ref: refs.setFloating, style: floatingStyles, ...getFloatingProps(), className: twMerge('twds-z-[99999] twds-group twds-pointer-events-none', containerClassName) }, isOpen && (React.createElement("div", { className: className, style: style }, label))))));
}
//# sourceMappingURL=Tooltip.js.map